/**
 * Utility to access authenticated user details
 * This can be used outside of React components
 */
import { appRoles } from '@/config/Config'
import store from '@/store'

/**
 * Get current authenticated user details
 * @returns {Object|null} User details object or null if not authenticated
 */
export const getAuthenticatedUser = () => {
  const state = store.getState()
  return state.authenticatedUser?.user || null
}

/**
 * Get active views (role and scopes)
 * Computed from user details instead of stored separately
 * @returns {Object|null} Active views object with role and scope or null
 */
export const getActiveViews = () => {
  const state = store.getState()
  const user = state.authenticatedUser?.user

  if (!user) return null
  const roleMap = {
    1: appRoles.Superadmin,
    2: appRoles.TenantAdmin,
    3: appRoles.CompanyAdmin,
    6: appRoles.Salesperson
  }
  const role = roleMap[user?.user_role_id] === user?.role ? roleMap[user?.user_role_id] : null

  return {
    role,
    scope: user?.roles_scopes || []
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const isUserAuthenticated = () => {
  const state = store.getState()
  return state.authenticatedUser?.isAuthenticated || false
}

/**
 * Get user's role
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  const activeViews = getActiveViews()
  return activeViews?.role || null
}

/**
 * Get user's scopes
 * @returns {Array} Array of user scopes
 */
export const getUserScopes = () => {
  const activeViews = getActiveViews()
  return activeViews?.scope || []
}

/**
 * Get user's email
 * @returns {string|null} User email or null
 */
export const getUserEmail = () => {
  const user = getAuthenticatedUser()
  return user?.email?.[0] || null
}

/**
 * Get user's name
 * @returns {string|null} User name or null
 */
export const getUserName = () => {
  const user = getAuthenticatedUser()
  return user?.name || null
}

/**
 * Get user's ID
 * @returns {string|number|null} User ID or null
 */
export const getUserId = () => {
  const user = getAuthenticatedUser()
  return user?.id || null
}

/**
 * Get distributor details
 * @returns {Object|null} Distributor object or null
 */
export const getDistributor = () => {
  const user = getAuthenticatedUser()
  return user?.distributor || null
}

/**
 * Get distributor ID
 * @returns {string|number|null} Distributor ID or null
 */
export const getDistributorId = () => {
  const user = getAuthenticatedUser()
  return user?.distributor?.id || null
}

// Example usage in a non-React file:
// import { getAuthenticatedUser, getUserRole, isUserAuthenticated } from './utilities/getUserFromStore'
//
// const user = getAuthenticatedUser()
// console.log('Current user:', user)
//
// const role = getUserRole()
// console.log('User role:', role)
//
// if (isUserAuthenticated()) {
//   console.log('User is authenticated')
// }
